import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { isPauseChosen, setPause, setPostnordOff } from '@/lib/history'
import type { Customer, HistoryWorklistEntry } from '@/api/types'

const DEFAULT_POSTNORD_OFF_WHY = 'avstängt av teamet från Historik-sidan'
const DEFAULT_PAUSE_WHY = 'pausat av teamet från Historik-sidan'

interface HistoryForwardCardProps {
  customer: Customer
  entry: HistoryWorklistEntry
  isDisabled: boolean
  isApplied: boolean
  waitingForPostnord: boolean
  enablePostnord: boolean
  enableBilling: boolean
  onChange: (entry: HistoryWorklistEntry) => void
  onEnablePostnordChange: (value: boolean) => void
  onEnableBillingChange: (value: boolean) => void
}

export function HistoryForwardCard({
  customer,
  entry,
  isDisabled,
  isApplied,
  waitingForPostnord,
  enablePostnord,
  enableBilling,
  onChange,
  onEnablePostnordChange,
  onEnableBillingChange
}: HistoryForwardCardProps): React.ReactElement {
  const subscription = customer.subscription
  const isPostnordOff = entry.postnordOff !== null
  const isPauseSelected = isPauseChosen(entry)
  const isAlreadyPaused = subscription !== null && subscription.paused_at !== null
  const canChoosePause = !isAlreadyPaused && !isApplied
  const hasBilling = entry.billThroughPlatform.length > 0
  const postnordNow = subscription?.enable_postnord ? 'på' : 'av'
  const capwayNow = subscription?.enable_capway ? 'på' : 'av'
  const canSwitchOn = !isPostnordOff && waitingForPostnord && !isApplied
  const showBilling = hasBilling && !isApplied

  const handlePostnordOffToggle = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(setPostnordOff(entry, event.target.checked ? (entry.postnordOff?.why ?? DEFAULT_POSTNORD_OFF_WHY) : null))
  }

  const handlePauseToggle = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(setPause(entry, event.target.checked ? (entry.pause?.why ?? DEFAULT_PAUSE_WHY) : null))
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle>4. Leveranser och fakturering framåt</CardTitle>
        <CardDescription>
          {isApplied && `Inlagd. I plattformen nu: leveranser ${postnordNow}, fakturering ${capwayNow}.`}
          {!isApplied &&
            `I plattformen nu: leveranser ${postnordNow}, fakturering ${capwayNow}. Här bestäms hur det ska vara efter inläsningen.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {isAlreadyPaused && (
          <p className="border border-amber-300 bg-amber-50/50 px-3 py-2 text-amber-900">
            Abonnemanget är pausat. Det återupptas med Återuppta på kundsidan.
          </p>
        )}
        {canChoosePause && (
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 size-4 accent-primary"
              checked={isPauseSelected}
              disabled={isDisabled}
              onChange={handlePauseToggle}
            />
            <span>
              <span className="font-medium">Pausa abonnemanget</span>
              <span className="block text-xs text-muted-foreground">
                Kunden vill ha ett uppehåll, eller leveranserna ska vänta tills något är klart. Inga nya leveranser skapas förrän
                abonnemanget återupptas på kundsidan; där fortsätter det sedan där det var.
              </span>
            </span>
          </label>
        )}
        {isPauseSelected && (
          <Input
            value={entry.pause?.why ?? ''}
            disabled={isDisabled}
            onChange={(event) => onChange(setPause(entry, event.target.value))}
            aria-label="Varför abonnemanget pausas"
          />
        )}
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 size-4 accent-primary"
            checked={isPostnordOff}
            disabled={isDisabled}
            onChange={handlePostnordOffToggle}
          />
          <span>
            <span className="font-medium">Leveranserna ska vara av</span>
            <span className="block text-xs text-muted-foreground">
              Bara när kunden inte ska få leveranser av ett annat skäl än en paus. Plattformen bokar inget förrän de slås på igen.
            </span>
          </span>
        </label>
        {isPostnordOff && (
          <Input
            value={entry.postnordOff?.why ?? ''}
            disabled={isDisabled}
            onChange={(event) => onChange(setPostnordOff(entry, event.target.value))}
            aria-label="Varför leveranserna är av"
          />
        )}
        {canSwitchOn && (
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 size-4 accent-primary"
              checked={enablePostnord}
              disabled={isDisabled}
              onChange={(event) => onEnablePostnordChange(event.target.checked)}
            />
            <span>
              <span className="font-medium">Slå på leveranserna efter inläsningen</span>
              <span className="block text-xs text-muted-foreground">
                Leveranserna är av för kunden i dag, så motorn hoppar över henne. Påslagna bokar plattformen nästa leverans på dess dag,
                till och med den sista enligt läget ovan.
              </span>
            </span>
          </label>
        )}
        {showBilling && (
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 size-4 accent-primary"
              checked={enableBilling}
              disabled={isDisabled}
              onChange={(event) => onEnableBillingChange(event.target.checked)}
            />
            <span>
              <span className="font-medium">
                Fakturera leverans {entry.billThroughPlatform.map((bill) => bill.sequence).join(', ')} via plattformen
              </span>
              <span className="block text-xs text-muted-foreground">
                Faktureringen slås på och fakturan skapas hos Capway vid nästa körning.
              </span>
            </span>
          </label>
        )}
      </CardContent>
    </Card>
  )
}
